import { Drawer, Stack, useTheme, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import { CloseIcon, MenuIcon } from 'ui/SvgIcon';

import { CountryCodeEnum, type CharityVettingFormValues } from '../../../types/charityVettingTypes';
import { ampliEventsMapper, handleStepNameForAmpliEvents } from '../../../utils/amplitudeHandlers';
import {
  progressChecker,
  matchCurrentPathNameToNavigationChange,
} from '../../../utils/commonNavigationHandlers';
import { navigationItems } from '../../../utils/countrySpecificNavigationHandlers';
import { type CharityVettingNavigationItem } from '../../../utils/navigationTypes';
import ContactUsDialog from '../contact-us-dialog/ContactUsDialog';
import DonationsPlusLogo from '../donations-plus-logo/DonationsPlusLogo';

import MenuContent from './MenuContent';
import msg from './messages';
import {
  StyledMobileHeader,
  StyledLeftSideHeader,
  StyledLeftSideWrapper,
  StyledMobileMenuHeader,
  StyledMobileMenu,
} from './StyledComponents';

interface NavigationBarProps {
  isMobile: boolean;
}

const NavigationBar = ({ isMobile }: NavigationBarProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { palette, spacing } = useTheme();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { control } = useFormContext<CharityVettingFormValues>();
  const countryIsoCode = useWatch({ control, name: 'charityOnboarding.countryIsoCode' });
  const flowProgress = useWatch({ control, name: 'flowManagement.flowProgress' });
  const localisedFlowProgress = flowProgress?.[countryIsoCode ?? CountryCodeEnum.GB];
  const localisedNavigationItems = navigationItems(countryIsoCode ?? CountryCodeEnum.GB);
  const [openDialog, setOpenDialog] = useState(false);

  const [menuItems, setMenuItems] = useState(localisedNavigationItems);
  const activeItem = menuItems.find((item) => !!item.isActive);

  const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);

  useEffect(() => {
    setMenuItems(
      menuItems.map((item) => ({
        ...item,
        isCompleted: progressChecker(item.id, localisedFlowProgress),
        isActive: matchCurrentPathNameToNavigationChange(pathname, item.urlTypeId),
      })),
    );
  }, [pathname]); //eslint-disable-line react-hooks/exhaustive-deps

  const toggleDialog = () => {
    setOpenDialog(!openDialog);
    if (openDialog && countryIsoCode) {
      const currentStepName = handleStepNameForAmpliEvents(location.pathname);
      ampliEventsMapper({
        eventName: 'charity_onboarding_contact_us_clicked',
        countryIsoCode,
        data: { contactUsSection: currentStepName },
      });
    }
  };

  const handleStepsProgress = (item: CharityVettingNavigationItem) => {
    const isCurrentStepCompleted = progressChecker(item.id, localisedFlowProgress);
    const currentStepIndex = menuItems.indexOf(item);
    const isFirstStep = currentStepIndex === 0;

    if (isFirstStep) {
      return isCurrentStepCompleted;
    }

    const previousSteps = menuItems.slice(0, currentStepIndex);
    const allPreviousStepsCompleted = previousSteps.every((step) =>
      progressChecker(step.id, localisedFlowProgress),
    );

    return allPreviousStepsCompleted;
  };

  const handleOnClick = (item: CharityVettingNavigationItem) => {
    if (!handleStepsProgress(item)) {
      return;
    }
    navigate(item.stepPathUrl);
    setMenuItems(
      menuItems.map((menuItem) =>
        menuItem.id === item.id
          ? {
              ...menuItem,
              isActive: true,
              isCompleted: progressChecker(menuItem.id, localisedFlowProgress),
            }
          : {
              ...menuItem,
              isActive: false,
              isCompleted: progressChecker(menuItem.id, localisedFlowProgress),
            },
      ),
    );
  };

  const handleFocusNavigation = (direction: 'up' | 'down') => {
    const activeIndex = menuItems.findIndex((item) => item.isActive);

    if (direction === 'up') {
      return activeIndex > 0 ? activeIndex - 1 : menuItems.length - 1;
    }
    if (direction === 'down') {
      return (activeIndex + 1) % menuItems.length;
    }
  };

  return (
    <>
      {isMobile ? (
        <>
          <StyledMobileHeader>
            <Stack spacing={0.5}>
              {activeItem && menuItems.length && (
                <>
                  <Typography variant='body2' sx={{ lineHeight: spacing(1.5) }}>
                    <FormattedMessage
                      {...msg.charityVettingHeaderStepTrackerText}
                      values={{
                        step: menuItems.indexOf(activeItem) + 1,
                        totalSteps: menuItems.length,
                      }}
                    />
                  </Typography>
                  <Typography variant='h3'>{activeItem?.stepLabel}</Typography>
                </>
              )}
            </Stack>
            <Stack>
              <MenuIcon sx={{ cursor: 'pointer' }} onClick={() => setIsDrawerOpen(!isDrawerOpen)} />
            </Stack>
          </StyledMobileHeader>
          <Drawer anchor={'right'} open={isDrawerOpen} onClose={toggleDrawer}>
            <StyledMobileMenu onClick={toggleDrawer}>
              <StyledMobileMenuHeader>
                <CloseIcon
                  fill={palette.cvi.neutrals[10]}
                  sx={{
                    cursor: 'pointer',
                  }}
                />
              </StyledMobileMenuHeader>
              <MenuContent
                menuItems={menuItems}
                onToggleDialog={toggleDialog}
                onClick={handleOnClick}
                onFocusNavigation={handleFocusNavigation}
                onStepsProgress={handleStepsProgress}
              />
            </StyledMobileMenu>
          </Drawer>
        </>
      ) : (
        <StyledLeftSideWrapper>
          <StyledLeftSideHeader>
            <DonationsPlusLogo width={spacing(20)} />
          </StyledLeftSideHeader>
          <MenuContent
            menuItems={menuItems}
            onClick={handleOnClick}
            onToggleDialog={toggleDialog}
            onFocusNavigation={handleFocusNavigation}
            onStepsProgress={handleStepsProgress}
          />
        </StyledLeftSideWrapper>
      )}
      <ContactUsDialog openDialog={openDialog} toggleDialog={toggleDialog} />
    </>
  );
};

export default NavigationBar;
